'use client';

/**
 * Canonical POSITION card — the mockup-#01/#02 surface rendered on market
 * detail (and, via `<WinnerPositionPanel>`, the winner page). One block per
 * outcome. Held rows come from the exact portfolio snapshot; fully exited
 * rows come from booked indexed position history. The market and winner
 * panels select canonical display rows and pass them into this surface.
 *
 * Current value and unrealised P&L come from the row's indexed `displayMark`;
 * spot price comes from indexed `currentPriceD9`. Chain reads remain isolated
 * to quick-trade execution preflight. Resolved states (won / lost / sold) come
 * from `resultState`. Every state shares one layout — the Unrealised header
 * cell + 2x2 stat grid — with quick-trade chips only while open and a drained
 * note on a loss (MOR-624). Prize-pool detail lives in the dedicated prize-pool
 * card below this one, not here.
 */
import { c as _c } from "react/compiler-runtime";
import { positionQuoteContext } from '@morfi/market-math';
import { useTranslations } from 'next-intl';
import { type ReactNode, useRef } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { PositionQuickTrade, type PositionQuickTradeChip } from '@/components/market/position-quick-trade';
import { SharePnlButton } from '@/components/portfolio/share/share-pnl-button';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { CardSectionLabel } from '@/components/ui/card-section-label';
import { Icon } from '@/components/ui/icon';
import { Alert02Icon, TradeUpIconSolid } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { PnlDirectionArrow } from '@/components/ui/pnl-direction-arrow';
import { ShrinkToFit, ShrinkToFitGroup } from '@/components/ui/shrink-to-fit';
import { type OutcomeColorClasses, type OutcomeHueName, outcomeHueToStepVar, useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { PositionCardRow } from '@/hooks/positions/user-portfolio/display-types';
import { Link } from '@/i18n/navigation';
import { breakEvenMarketCapD6 } from '@/lib/chart/sdk-mapping';
import { type FormatMode, useFormattedCurrency, useQuickAmounts } from '@/lib/currency';
import { PNL_DUST_D6 } from '@/lib/market/position-pnl';
import { hasActiveSnapshotOutcomeBalance, hasPositiveSnapshotOutcomeBalance } from '@/lib/market/position-visibility';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { claimRightsMintAddress } from '@/lib/solana/claim-rights-mint';
import { computeChipBuyPlan, computeChipSellPlan } from '@/lib/trading/quick-trade-execution';
import { confettiOriginFromElement } from '@/lib/ui/confetti';
import { cn } from '@/lib/ui/utils';
import { useTradeExecutor } from '@/providers/trade-executor-provider';
import { type OnchainTradeData, useOnchainTradeData } from '@/queries/onchain-trade-data';
import { useTradeBalances } from '@/queries/trade-balances';
import { useChartSettingsStore } from '@/stores/chart-settings';
const ZERO: bigint = BigInt(0);
const MAX_SAFE_ANIMATED_D6: bigint = BigInt(Number.MAX_SAFE_INTEGER);
const POSITION_QUICK_BUY_TIER_INDEXES = [0, 1, 3, 4] as const;
const POSITION_QUICK_SELL_PERCENTAGES = [25, 50, 75, 100] as const;
function bi(v: string | undefined): bigint {
  // Backend money fields are integer D6/D9 strings; guard with a validity test
  // rather than try/catch so a malformed wire value falls back to 0 without
  // exception-as-control-flow (FT-12).
  if (v === undefined || !/^-?\d+$/.test(v)) return ZERO;
  return BigInt(v);
}
function unsignedWire(v: string | null): bigint | null {
  if (v === null || !/^(?:0|[1-9]\d*)$/.test(v)) return null;
  return BigInt(v);
}

/** A D6 value that ticks only while every intermediate integer is exactly representable. */
export function AnimatedD6(t0) {
  const $ = _c(19);
  const {
    d6,
    signed: t1,
    mode
  } = t0;
  const signed = t1 === undefined ? false : t1;
  const {
    formatD6,
    formatD6Signed
  } = useFormattedCurrency();
  const fmt = signed ? formatD6Signed : formatD6;
  let t2;
  if ($[0] !== d6 || $[1] !== fmt || $[2] !== mode) {
    t2 = fmt(d6, mode);
    $[0] = d6;
    $[1] = fmt;
    $[2] = mode;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const exactDisplay = t2;
  let t3;
  if ($[4] !== d6) {
    t3 = /^-?(?:0|[1-9]\d*)$/.test(d6) ? BigInt(d6) : null;
    $[4] = d6;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const parsedD6 = t3;
  if (parsedD6 === null || parsedD6 < -MAX_SAFE_ANIMATED_D6 || parsedD6 > MAX_SAFE_ANIMATED_D6) {
    let t4;
    if ($[6] !== exactDisplay) {
      t4 = <span className="tabular-nums">{exactDisplay}</span>;
      $[6] = exactDisplay;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t4;
  if ($[8] !== exactDisplay) {
    t4 = <span className="sr-only">{exactDisplay}</span>;
    $[8] = exactDisplay;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = Number(parsedD6);
  let t6;
  if ($[10] !== fmt || $[11] !== mode) {
    t6 = intermediateD6 => fmt(BigInt(Math.round(intermediateD6)).toString(), mode);
    $[10] = fmt;
    $[11] = mode;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <span aria-hidden="true"><AnimatedNumber value={t5} format={t6} /></span>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t4 || $[17] !== t7) {
    t8 = <span className="tabular-nums">{t4}{t7}</span>;
    $[16] = t4;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
}

/** A D9 price that ticks only while every intermediate integer is exactly representable. */
export function AnimatedD9(t0) {
  const $ = _c(17);
  const {
    d9
  } = t0;
  const {
    formatD9
  } = useFormattedCurrency();
  let t1;
  if ($[0] !== d9 || $[1] !== formatD9) {
    t1 = formatD9(d9);
    $[0] = d9;
    $[1] = formatD9;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const exactDisplay = t1;
  let t2;
  if ($[3] !== d9) {
    t2 = /^(?:0|[1-9]\d*)$/.test(d9) ? BigInt(d9) : null;
    $[3] = d9;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const parsedD9 = t2;
  if (parsedD9 === null || parsedD9 > MAX_SAFE_ANIMATED_D6) {
    let t3;
    if ($[5] !== exactDisplay) {
      t3 = <span className="tabular-nums">{exactDisplay}</span>;
      $[5] = exactDisplay;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t3;
  if ($[7] !== exactDisplay) {
    t3 = <span className="sr-only">{exactDisplay}</span>;
    $[7] = exactDisplay;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = Number(parsedD9);
  let t5;
  if ($[9] !== formatD9) {
    t5 = intermediateD9 => formatD9(BigInt(Math.round(intermediateD9)).toString());
    $[9] = formatD9;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <span aria-hidden="true"><AnimatedNumber value={t4} format={t5} /></span>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t6) {
    t7 = <span className="tabular-nums">{t3}{t6}</span>;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/** Per-outcome market detail data supplied by `useGetMarket`.
 *  Current market cap comes from the backend; indexed supply anchors the
 *  graduated average-entry conversion. Image fields are App-DB fallbacks. */
export interface OutcomeMarketData {
  effectiveMarketCapD6: string;
  /** Indexed supply used by the graduated-token price→market-cap contract. */
  indexedOutcomeMintSupplyD6: string;
  /** Fallback image URL from the App-DB market-detail cache, used when the
   *  Timescale positions row has a null `outcomeImageUrl` (the Timescale
   *  `outcome.image_url` is NATS-owned and may lag the App-DB copy). */
  imageUrl?: string | null;
  imageNsfw?: boolean;
}
interface PositionCardProps {
  /** Exact display rows selected by the market or winner panel. */
  rows: readonly PositionCardRow[];
  /** Default market for blocks whose snapshot row is scoped by outcome mint. */
  market?: string;
  /** Tradeable-token route for the "Trade winner" CTA, keyed by outcomeMint. */
  winnerHref?: (row: PositionCardRow) => string;
  /** When true (chart y-axis = market cap), the current-price stat renders the
   *  backend-provided effective market cap. */
  marketCapMode?: boolean;
  /** Per-outcome effective market caps keyed by outcome index. */
  outcomeMarket?: Record<number, OutcomeMarketData>;
  /** When true (winner-detail page), won_holding positions use the 4-tile layout
   *  matching market-detail open positions, and buy chips render in success-green
   *  instead of outcome color. */
  winnerPage?: boolean;
  /** The market's full outcome count. When set, every block keys its on-chain
   *  trade query on the SAME count — sharing one cache entry with the page-level
   *  fetch instead of one overlapping-prefix entry per held outcome. */
  outcomeCount?: number;
  /** Exact pool snapshot already owned by the winner detail cache. */
  tradeData?: OnchainTradeData;
  className?: string;
}
export function PositionCard(t0) {
  const $ = _c(22);
  const {
    rows,
    market,
    winnerHref,
    marketCapMode: t1,
    outcomeMarket,
    winnerPage: t2,
    outcomeCount,
    tradeData,
    className
  } = t0;
  const marketCapMode = t1 === undefined ? false : t1;
  const winnerPage = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== market) {
    t3 = market !== undefined ? {
      market
    } : {};
    $[0] = market;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== outcomeMarket) {
    t4 = outcomeMarket !== undefined ? {
      outcomeMarket
    } : {};
    $[2] = outcomeMarket;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== winnerHref) {
    t5 = winnerHref !== undefined ? {
      winnerHref
    } : {};
    $[4] = winnerHref;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== outcomeCount) {
    t6 = outcomeCount !== undefined ? {
      outcomeCount
    } : {};
    $[6] = outcomeCount;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== tradeData) {
    t7 = tradeData !== undefined ? {
      tradeData
    } : {};
    $[8] = tradeData;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== className) {
    t8 = className !== undefined ? {
      className
    } : {};
    $[10] = className;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== marketCapMode || $[13] !== rows || $[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== winnerPage) {
    t9 = <PositionCardView rows={rows} {...t3} marketCapMode={marketCapMode} {...t4} {...t5} winnerPage={winnerPage} {...t6} {...t7} {...t8} />;
    $[12] = marketCapMode;
    $[13] = rows;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = winnerPage;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
export interface PositionCardViewProps {
  /** Already-resolved position rows (one per held or fully exited outcome). */
  rows: readonly PositionCardRow[];
  /** Default market for blocks whose `row.market` is absent. */
  market?: string;
  marketCapMode?: boolean;
  outcomeMarket?: Record<number, OutcomeMarketData>;
  winnerHref?: (row: PositionCardRow) => string;
  winnerPage?: boolean;
  /** Full outcome count for the shared on-chain trade query (see `PositionCardProps`). */
  outcomeCount?: number;
  tradeData?: OnchainTradeData;
  className?: string;
}

/**
 * Presentational position card — the Panel + header + one `PositionBlock` per
 * outcome, for an already-selected display row set. The market and
 * winner panels own selection; the workspace showcase renders this directly
 * with canonical display fixtures to exercise every result state.
 */
export function PositionCardView(t0) {
  const $ = _c(28);
  const {
    rows,
    market,
    marketCapMode: t1,
    outcomeMarket,
    winnerHref,
    winnerPage: t2,
    outcomeCount,
    tradeData,
    className
  } = t0;
  const marketCapMode = t1 === undefined ? false : t1;
  const winnerPage = t2 === undefined ? false : t2;
  const t = useTranslations("market.position");
  const ordered = sortDisplayPositionsActiveFirst(rows);
  const colorClasses = useOutcomeColor(ordered.map(_temp));
  if (rows.length === 0) {
    return null;
  }
  let t3;
  if ($[0] !== rows || $[1] !== winnerPage) {
    t3 = resolveDisplayPositionHeadState(rows, {
      winnerPage
    });
    $[0] = rows;
    $[1] = winnerPage;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const headState = t3;
  const T0 = Panel;
  let t4;
  if ($[3] !== className) {
    t4 = cn("flex flex-col gap-0 overflow-hidden p-0", className);
    $[3] = className;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== headState) {
    t5 = headState === "closed" ? undefined : <span aria-hidden={true} className="relative inline-flex size-2"><span className="absolute inset-0 animate-ping rounded-full bg-feedback-success-accent opacity-60" /><span className="absolute inset-0 m-auto size-1.5 rounded-full bg-feedback-success-accent" /></span>;
    $[5] = headState;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== headState || $[8] !== t) {
    t6 = headState === "held" ? t("head") : headState === "resolved" ? t("headResolved") : t("headClosed");
    $[7] = headState;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <CardSectionLabel icon={TradeUpIconSolid} className="px-4 pt-3 pb-2.5" afterTitle={t5}>{t6}</CardSectionLabel>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== colorClasses || $[14] !== market || $[15] !== marketCapMode || $[16] !== outcomeCount || $[17] !== outcomeMarket || $[18] !== t || $[19] !== tradeData || $[20] !== winnerHref || $[21] !== winnerPage) {
    t8 = (row, i) => {
      const color = colorClasses[i];
      if (color === undefined) {
        return null;
      }
      if (!hasPositiveSnapshotOutcomeBalance(row)) {
        return <ExitedPositionRow key={row.positionId} row={row} color={color} outcomeData={outcomeMarket?.[row.outcomeIndex]} t={t} />;
      }
      return <PositionBlock key={row.positionId} row={row} color={color} market={market ?? row.market} marketCapMode={marketCapMode} outcomeData={outcomeMarket?.[row.outcomeIndex]} winnerPage={winnerPage} {...outcomeCount !== undefined ? {
        outcomeCount
      } : {}} {...tradeData !== undefined ? {
        tradeData
      } : {}} {...winnerHref !== undefined ? {
        winnerHref
      } : {}} t={t} />;
    };
    $[13] = colorClasses;
    $[14] = market;
    $[15] = marketCapMode;
    $[16] = outcomeCount;
    $[17] = outcomeMarket;
    $[18] = t;
    $[19] = tradeData;
    $[20] = winnerHref;
    $[21] = winnerPage;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const t9 = ordered.map(t8);
  let t10;
  if ($[23] !== T0 || $[24] !== t4 || $[25] !== t7 || $[26] !== t9) {
    t10 = <T0 className={t4}>{t7}{t9}</T0>;
    $[23] = T0;
    $[24] = t4;
    $[25] = t7;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  return t10;
}
function _temp(r) {
  return {
    title: r.outcomeLabel ?? `Outcome ${r.outcomeIndex}`,
    index: r.outcomeIndex
  };
}
interface BlockProps {
  row: PositionCardRow;
  color: OutcomeColorClasses;
  market: string;
  marketCapMode: boolean;
  outcomeData?: OutcomeMarketData | undefined;
  winnerHref?: (row: PositionCardRow) => string;
  winnerPage: boolean;
  outcomeCount?: number | undefined;
  tradeData?: OnchainTradeData | undefined;
  t: ReturnType<typeof useTranslations>;
}
function PositionBlock(t0) {
  const $ = _c(89);
  const {
    row,
    color,
    market,
    marketCapMode,
    outcomeData,
    winnerHref,
    winnerPage,
    outcomeCount,
    tradeData,
    t
  } = t0;
  let t1;
  if ($[0] !== market) {
    t1 = s => s.autoTradePending[market] ?? null;
    $[0] = market;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const autoPending = useChartSettingsStore(t1);
  const quickTradeProgress = autoPending !== null && autoPending.outcomeIndex === row.outcomeIndex ? autoPending : null;
  const executor = useTradeExecutor();
  const {
    toUsdc
  } = useFormattedCurrency();
  const network = useActiveNetwork();
  const hasHolding = hasPositiveSnapshotOutcomeBalance(row);
  let t2;
  if ($[2] !== executor || $[3] !== row || $[4] !== winnerPage) {
    t2 = executor !== null && (hasActiveSnapshotOutcomeBalance(row) || winnerPage);
    $[2] = executor;
    $[3] = row;
    $[4] = winnerPage;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const chipsActive = t2;
  let t3;
  if ($[6] !== chipsActive || $[7] !== row.outcomeIndex || $[8] !== row.outcomeMint || $[9] !== tradeData) {
    t3 = tradeData === undefined && chipsActive ? [{
      outcomeIndex: row.outcomeIndex,
      outcomeMint: row.outcomeMint
    }] : [];
    $[6] = chipsActive;
    $[7] = row.outcomeIndex;
    $[8] = row.outcomeMint;
    $[9] = tradeData;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const poolQuery = useOnchainTradeData(tradeData === undefined && chipsActive ? market : undefined, tradeData === undefined && chipsActive ? outcomeCount : undefined, t3);
  const td = tradeData ?? poolQuery.data;
  const poolUnavailable = tradeData === undefined && (poolQuery.isError || poolQuery.isStale);
  const checkedPoolContext = td !== undefined ? positionQuoteContext(td, row.market, row.outcomeIndex, row.outcomeMint, poolUnavailable) : null;
  const poolContext = checkedPoolContext?.available === true ? checkedPoolContext.value : null;
  const claimRightsMint = poolContext?.poolModel === "bonding_curve" ? claimRightsMintAddress(market, row.outcomeIndex, network) : undefined;
  const walletBalancesQuery = useTradeBalances(chipsActive ? row.wallet : undefined, chipsActive ? row.outcomeMint : undefined, chipsActive ? claimRightsMint : undefined);
  const exactWalletBalances = walletBalancesQuery.isPlaceholderData === true || walletBalancesQuery.isError || walletBalancesQuery.isStale ? undefined : walletBalancesQuery.data;
  const onchainOutcomeBalanceD6 = rawBalanceD6(exactWalletBalances?.outcomeRawBalance);
  const onchainClaimRightsBalanceD6 = claimRightsMint === undefined ? null : rawBalanceD6(exactWalletBalances?.claimRightsRawBalance);
  const valueD6 = row.displayMark.currentValueD6;
  const unrealisedD6 = row.displayMark.unrealisedPnlD6;
  let t4;
  if ($[11] !== row.realizedPnlD6) {
    t4 = bi(row.realizedPnlD6);
    $[11] = row.realizedPnlD6;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const realized = t4;
  const isWon = row.resultState === "won_holding" || row.resultState === "won_sold";
  const isLost = row.resultState === "lost";
  const avgEntryD9 = row.displayMark.averageEntryPriceD9;
  let t5;
  if ($[13] !== row.currentPriceD9) {
    t5 = unsignedWire(row.currentPriceD9);
    $[13] = row.currentPriceD9;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const spotPriceD9 = t5;
  const noLiveHoldings = !hasHolding;
  const headerUnreal = noLiveHoldings ? ZERO : unrealisedD6;
  const blockRef = useRef(null);
  let t6;
  if ($[15] !== outcomeData) {
    t6 = outcomeData ? bi(outcomeData.effectiveMarketCapD6) : ZERO;
    $[15] = outcomeData;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const effectiveMarketCapD6 = t6;
  const showMcap = marketCapMode && effectiveMarketCapD6 > ZERO;
  let t7;
  if ($[17] !== avgEntryD9 || $[18] !== outcomeData || $[19] !== row.outcomeIndex || $[20] !== td) {
    t7 = averageMarketCapD6({
      averageEntryPriceD9: avgEntryD9,
      outcomeIndex: row.outcomeIndex,
      outcomeData,
      tradeData: td
    });
    $[17] = avgEntryD9;
    $[18] = outcomeData;
    $[19] = row.outcomeIndex;
    $[20] = td;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const averageEntryMarketCapD6 = t7;
  const buyChip = function buyChip(localAmount) {
    if (executor === null || poolContext === null) {
      return;
    }
    const plan = computeChipBuyPlan({
      buyUsdc: toUsdc(localAmount),
      context: poolContext
    });
    if (plan === null) {
      return;
    }
    executor.executeBuy({
      marketAddress: market,
      outcomeIndex: row.outcomeIndex,
      outcomeMint: row.outcomeMint,
      amountInD6: plan.amountInD6,
      walletDebitD6: plan.walletDebitD6,
      feeExcludedQuoteInputD6: plan.feeExcludedQuoteInputD6,
      outcomeTokensOutD6: plan.outcomeTokensOutD6,
      desiredAmountD6: plan.desiredAmountD6,
      slippageBps: plan.slippageBps,
      suggestedSlippageBps: plan.suggestedSlippageBps,
      marketPhase: plan.marketPhase,
      confettiOrigin: confettiOriginFromElement(blockRef.current),
      chip: {
        outcomeIndex: row.outcomeIndex,
        direction: "buy",
        buyUsdc: localAmount
      }
    });
  };
  const sellChip = function sellChip(pct) {
    if (executor === null || poolContext === null) {
      return;
    }
    const plan_0 = computeChipSellPlan({
      pct,
      indexedBalanceD6: bi(row.balanceD6),
      claimRightsBalanceD6: bi(row.claimRightsBalanceD6),
      onchainBalanceD6: onchainOutcomeBalanceD6,
      onchainClaimRightsBalanceD6,
      context: poolContext
    });
    if (plan_0 === null) {
      return;
    }
    executor.executeSell({
      marketAddress: market,
      marketTitle: row.marketTitle ?? undefined,
      outcomeIndex: row.outcomeIndex,
      outcomeMint: row.outcomeMint,
      outcomeLabel: row.outcomeLabel ?? `Outcome ${row.outcomeIndex}`,
      tokensNeededD6: plan_0.tokensNeededD6,
      expectedNetUsdcOutD6: plan_0.expectedNetUsdcOutD6,
      grossQuoteAmountD6: plan_0.grossQuoteAmountD6,
      feeExcludedQuoteOutputD6: plan_0.feeExcludedQuoteOutputD6,
      sellPenaltyFeeD6: plan_0.penaltyFeeD6,
      positionAccounting: {
        balanceD6: row.balanceD6,
        remainingCostBasisNetD6: row.remainingCostBasisNetD6,
        claimRightsBalanceD6: row.claimRightsBalanceD6,
        realizedPnlD6: row.realizedPnlD6,
        realizedCostBasisD6: row.realizedCostBasisD6
      },
      slippageBps: plan_0.slippageBps,
      suggestedSlippageBps: plan_0.suggestedSlippageBps,
      marketPhase: plan_0.marketPhase,
      confettiOrigin: confettiOriginFromElement(blockRef.current),
      chip: {
        outcomeIndex: row.outcomeIndex,
        direction: "sell",
        sellPercent: pct
      }
    });
  };
  let t8;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      max: 5
    };
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const buyTiers = useQuickAmounts(t8);
  const openBuyChips = POSITION_QUICK_BUY_TIER_INDEXES.flatMap(index => {
    const amount = buyTiers[index];
    if (amount === undefined) {
      return [];
    }
    return [{
      kind: "buy",
      label: `+${amount.label}`,
      onClick: () => buyChip(amount.value),
      id: {
        direction: "buy",
        buyUsdc: amount.value
      },
      disabled: executor?.isBuySellBlocked === true
    }];
  });
  const quickSellAvailable = poolContext !== null && onchainOutcomeBalanceD6 !== null && (poolContext.poolModel !== "bonding_curve" || onchainClaimRightsBalanceD6 !== null);
  const openSellChips = POSITION_QUICK_SELL_PERCENTAGES.map(percentage => ({
    kind: "sell",
    label: `-${percentage}%`,
    onClick: () => sellChip(percentage),
    id: {
      direction: "sell",
      sellPercent: percentage
    },
    disabled: !quickSellAvailable || executor?.isBuySellBlocked === true
  }));
  const openChips = [...openBuyChips, ...openSellChips];
  const t9 = !isLost;
  const t10 = outcomeData?.imageUrl;
  const t11 = outcomeData?.imageNsfw;
  let t12;
  if ($[23] !== color.hue || $[24] !== isLost || $[25] !== row || $[26] !== t10 || $[27] !== t11 || $[28] !== t9) {
    t12 = <PositionThumb row={row} hue={color.hue} dim={isLost} showDot={t9} fallbackImageUrl={t10} fallbackImageNsfw={t11} />;
    $[23] = color.hue;
    $[24] = isLost;
    $[25] = row;
    $[26] = t10;
    $[27] = t11;
    $[28] = t9;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = isLost ? "text-foreground-tertiary" : "text-foreground-primary";
  let t14;
  if ($[30] !== t13) {
    t14 = cn("truncate text-body leading-tight", t13);
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== isLost || $[33] !== row.outcomeLabel || $[34] !== t) {
    t15 = isLost ? t("finalValue") : row.outcomeLabel ?? t("currentValue");
    $[32] = isLost;
    $[33] = row.outcomeLabel;
    $[34] = t;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t14 || $[37] !== t15) {
    t16 = <div className={t14}>{t15}</div>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = isWon && "text-feedback-success-content";
  let t18;
  if ($[39] !== t17) {
    t18 = cn("text-heading-lg font-bold tabular-nums", t17);
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== valueD6) {
    t19 = valueD6 ?? BigInt(0);
    $[41] = valueD6;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t19) {
    t20 = t19.toString();
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t20) {
    t21 = <AnimatedD6 d6={t20} />;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t18 || $[48] !== t21) {
    t22 = <div data-testid="position-current-value" className="mt-0.5 min-w-0"><ShrinkToFit contentClassName={t18}>{t21}</ShrinkToFit></div>;
    $[47] = t18;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t16 || $[51] !== t22) {
    t23 = <div className="min-w-0">{t16}{t22}</div>;
    $[50] = t16;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t) {
    t24 = t("unrealised");
    $[53] = t;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t24) {
    t25 = <div className="truncate text-body text-foreground-tertiary leading-tight">{t24}</div>;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== headerUnreal) {
    t26 = headerUnreal ?? BigInt(0);
    $[57] = headerUnreal;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t26) {
    t27 = <ShrinkToFit align="end" contentClassName="text-heading-lg font-bold tabular-nums"><PnlValue d6={t26} /></ShrinkToFit>;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t25 || $[62] !== t27) {
    t28 = <div className="min-w-0 text-right" data-testid="position-unrealised">{t25}{t27}</div>;
    $[61] = t25;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t12 || $[65] !== t23 || $[66] !== t28) {
    t29 = <ShrinkToFitGroup className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] items-start gap-3.5">{t12}{t23}{t28}</ShrinkToFitGroup>;
    $[64] = t12;
    $[65] = t23;
    $[66] = t28;
    $[67] = t29;
  } else {
    t29 = $[67];
  }
  const t30 = row.costBasisD6;
  const t31 = showMcap ? effectiveMarketCapD6 : null;
  let t32;
  if ($[68] !== row.realizedPenaltyD6) {
    t32 = bi(row.realizedPenaltyD6);
    $[68] = row.realizedPenaltyD6;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== averageEntryMarketCapD6 || $[71] !== avgEntryD9 || $[72] !== marketCapMode || $[73] !== realized || $[74] !== row.costBasisD6 || $[75] !== spotPriceD9 || $[76] !== t || $[77] !== t31 || $[78] !== t32) {
    t33 = <PositionMetrics investedD6={t30} marketCapMode={marketCapMode} currentMarketCapD6={t31} spotPriceD9={spotPriceD9} averageEntryD9={avgEntryD9} averageEntryMarketCapD6={averageEntryMarketCapD6} realizedD6={realized} penaltyD6={t32} t={t} />;
    $[70] = averageEntryMarketCapD6;
    $[71] = avgEntryD9;
    $[72] = marketCapMode;
    $[73] = realized;
    $[74] = row.costBasisD6;
    $[75] = spotPriceD9;
    $[76] = t;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== isLost || $[81] !== t) {
    t34 = isLost && <div className="mt-3.5 flex min-h-10 items-center justify-center gap-1.5 rounded-control border border-feedback-error-border bg-feedback-error-surface px-3 py-2 text-caption font-medium text-feedback-error-content"><Icon icon={Alert02Icon} className="size-3.5 shrink-0" /><span className="text-center">{t("drainedNote")}</span></div>;
    $[80] = isLost;
    $[81] = t;
    $[82] = t34;
  } else {
    t34 = $[82];
  }
  const t35 = hasActiveSnapshotOutcomeBalance(row) || winnerPage ? <PositionQuickTrade chips={openChips} color={color} progress={quickTradeProgress} greenBuyChips={winnerPage} className="mt-3" /> : isWon && winnerHref !== undefined ? <><AnimatedButton variant="primary" className="mt-4 w-full" asChild={true}><Link href={winnerHref(row)}>{t("tradeWinner")}</Link></AnimatedButton><p className="mt-2 text-center text-caption text-foreground-tertiary">{t("winnerCaption")}</p></> : null;
  let t36;
  if ($[83] !== blockRef || $[84] !== t29 || $[85] !== t33 || $[86] !== t34 || $[87] !== t35) {
    t36 = <div ref={blockRef} className="border-t border-border-primary px-4 pt-3.5 pb-4 first:border-t-0">{t29}{t33}{t34}{t35}</div>;
    $[83] = blockRef;
    $[84] = t29;
    $[85] = t33;
    $[86] = t34;
    $[87] = t35;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  return t36;
}
interface PositionMetricsProps {
  investedD6: string;
  marketCapMode: boolean;
  currentMarketCapD6: bigint | null;
  spotPriceD9: bigint | null;
  averageEntryD9: bigint | null;
  averageEntryMarketCapD6: bigint | null;
  realizedD6: bigint;
  /** Conviction penalty already booked into `realizedD6`; shown only when charged. */
  penaltyD6: bigint;
  t: ReturnType<typeof useTranslations>;
}
function PositionMetrics(t0) {
  const $ = _c(53);
  const {
    investedD6,
    marketCapMode,
    currentMarketCapD6,
    spotPriceD9,
    averageEntryD9,
    averageEntryMarketCapD6,
    realizedD6,
    penaltyD6,
    t
  } = t0;
  const feeT = useTranslations("trade.fees");
  let t1;
  if ($[0] !== currentMarketCapD6 || $[1] !== t) {
    t1 = currentMarketCapD6 === null ? t("spotPrice") : t("marketCap");
    $[0] = currentMarketCapD6;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const currentLabel = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <AnimatedD9 d9="0" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let currentValue = t2;
  if (currentMarketCapD6 !== null) {
    let t3;
    if ($[4] !== currentMarketCapD6) {
      t3 = currentMarketCapD6.toString();
      $[4] = currentMarketCapD6;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t3) {
      t4 = <AnimatedD6 d6={t3} mode="compactDecimal" />;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    currentValue = t4;
  } else {
    if (spotPriceD9 !== null) {
      let t3;
      if ($[8] !== spotPriceD9) {
        t3 = spotPriceD9.toString();
        $[8] = spotPriceD9;
        $[9] = t3;
      } else {
        t3 = $[9];
      }
      let t4;
      if ($[10] !== t3) {
        t4 = <AnimatedD9 d9={t3} />;
        $[10] = t3;
        $[11] = t4;
      } else {
        t4 = $[11];
      }
      currentValue = t4;
    }
  }
  let t3;
  if ($[12] !== marketCapMode || $[13] !== t) {
    t3 = marketCapMode ? t("avgMarketCap") : t("avgEntry");
    $[12] = marketCapMode;
    $[13] = t;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const averageLabel = t3;
  let t4;
  if ($[15] !== averageEntryD9 || $[16] !== averageEntryMarketCapD6 || $[17] !== marketCapMode) {
    t4 = marketCapMode ? averageEntryMarketCapD6 === null ? <AnimatedD6 d6="0" mode="compactDecimal" /> : <AnimatedD6 d6={averageEntryMarketCapD6.toString()} mode="compactDecimal" /> : averageEntryD9 === null ? <AnimatedD9 d9="0" /> : <AnimatedD9 d9={averageEntryD9.toString()} />;
    $[15] = averageEntryD9;
    $[16] = averageEntryMarketCapD6;
    $[17] = marketCapMode;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const averageValue = t4;
  let t5;
  if ($[19] !== t) {
    t5 = t("invested");
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== investedD6) {
    t6 = <AnimatedD6 d6={investedD6} />;
    $[21] = investedD6;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t5 || $[24] !== t6) {
    t7 = <Stat testId="position-invested" label={t5} value={t6} />;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== currentLabel || $[27] !== currentValue) {
    t8 = <Stat testId="position-spot-price" label={currentLabel} value={currentValue} />;
    $[26] = currentLabel;
    $[27] = currentValue;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== averageLabel || $[30] !== averageValue) {
    t9 = <Stat testId="position-average-entry" label={averageLabel} value={averageValue} />;
    $[29] = averageLabel;
    $[30] = averageValue;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== t) {
    t10 = t("realised");
    $[32] = t;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== realizedD6) {
    t11 = realizedD6.toString();
    $[34] = realizedD6;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t11) {
    t12 = <AnimatedD6 d6={t11} signed={true} />;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== realizedD6) {
    t13 = pnlTone(realizedD6);
    $[38] = realizedD6;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t10 || $[41] !== t12 || $[42] !== t13) {
    t14 = <Stat testId="position-realised" label={t10} value={t12} tone={t13} />;
    $[40] = t10;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== feeT || $[45] !== penaltyD6) {
    t15 = penaltyD6 > ZERO && <Stat testId="position-penalty" label={feeT("penalty_fee")} value={<AnimatedD6 d6={penaltyD6.toString()} />} />;
    $[44] = feeT;
    $[45] = penaltyD6;
    $[46] = t15;
  } else {
    t15 = $[46];
  }
  let t16;
  if ($[47] !== t14 || $[48] !== t15 || $[49] !== t7 || $[50] !== t8 || $[51] !== t9) {
    t16 = <div className="mt-4 grid grid-cols-2 gap-2">{t7}{t8}{t9}{t14}{t15}</div>;
    $[47] = t14;
    $[48] = t15;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  return t16;
}
function averageMarketCapD6({
  averageEntryPriceD9,
  outcomeIndex,
  outcomeData,
  tradeData
}: {
  averageEntryPriceD9: bigint | null;
  outcomeIndex: number;
  outcomeData: OutcomeMarketData | undefined;
  tradeData: OnchainTradeData | undefined;
}): bigint | null {
  if (averageEntryPriceD9 === null || tradeData === undefined) return null;
  const pool = tradeData.outcomes.find(outcome => outcome.outcomeIndex === outcomeIndex);
  if (pool === undefined) return null;
  return breakEvenMarketCapD6(averageEntryPriceD9, {
    graduated: pool.cpSwap !== null,
    indexedOutcomeMintSupplyD6: outcomeData?.indexedOutcomeMintSupplyD6,
    initialOutcomeSupplyD6: pool.bondingCurve?.initialOutcomeSupplyD6,
    virtualQuoteD6: pool.bondingCurve?.virtualQuoteReserveD6
  }) ?? null;
}
interface ExitedRowProps {
  row: PositionCardRow;
  color: OutcomeColorClasses;
  outcomeData?: OutcomeMarketData | undefined;
  t: ReturnType<typeof useTranslations>;
}

// A fully-exited position (sold-out / won-then-sold / lost) rendered as one
// header-style row that mirrors a held position's "Current value" cell but keyed
// to the realised result (MOR-665): a "Realised win / loss" label over the
// realised PnL, drawn with the same up/down arrow as the Unrealised cell (via
// `PnlValue`) rather than a +/- sign, plus a Share affordance. Resolution
// losses are booked into realized PnL by the indexer and never use the legacy
// linear unrealized field.
function ExitedPositionRow(t0) {
  const $ = _c(30);
  const {
    row,
    color,
    outcomeData,
    t
  } = t0;
  const label = row.outcomeLabel ?? `Outcome ${row.outcomeIndex}`;
  const isLost = row.resultState === "lost";
  const resultD6 = bi(row.realizedPnlD6);
  let t1;
  if ($[0] !== resultD6 || $[1] !== t) {
    t1 = resultD6 > BigInt(PNL_DUST_D6) ? t("realisedWin") : resultD6 < -BigInt(PNL_DUST_D6) ? t("realisedLoss") : t("realised");
    $[0] = resultD6;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const realisedLabel = t1;
  const t2 = !isLost;
  const t3 = outcomeData?.imageUrl;
  const t4 = outcomeData?.imageNsfw;
  let t5;
  if ($[3] !== color.hue || $[4] !== isLost || $[5] !== row || $[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = <PositionThumb row={row} hue={color.hue} dim={isLost} showDot={t2} fallbackImageUrl={t3} fallbackImageNsfw={t4} />;
    $[3] = color.hue;
    $[4] = isLost;
    $[5] = row;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== realisedLabel) {
    t6 = <div className="text-body text-foreground-tertiary leading-tight">{realisedLabel}</div>;
    $[10] = realisedLabel;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== resultD6) {
    t7 = <PnlValue d6={resultD6} className="text-heading" />;
    $[12] = resultD6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className="flex flex-1 flex-col justify-center self-stretch">{t6}{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = row.market;
  const t10 = row.marketTitle ?? row.market;
  const t11 = row.wallet;
  const t12 = row.outcomeIndex;
  let t13;
  if ($[17] !== resultD6) {
    t13 = resultD6.toString();
    $[17] = resultD6;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== label || $[20] !== row.market || $[21] !== row.outcomeIndex || $[22] !== row.wallet || $[23] !== t10 || $[24] !== t13) {
    t14 = <SharePnlButton marketPubkey={t9} marketTitle={t10} walletPubkey={t11} outcomeIndex={t12} outcomeLabel={label} heroPnlD6={t13} closed={true} showLabel={true} />;
    $[19] = label;
    $[20] = row.market;
    $[21] = row.outcomeIndex;
    $[22] = row.wallet;
    $[23] = t10;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14 || $[27] !== t5 || $[28] !== t8) {
    t15 = <div className="flex items-center gap-3.5 border-t border-border-primary px-4 pt-3.5 pb-4 first:border-t-0">{t5}{t8}{t14}</div>;
    $[26] = t14;
    $[27] = t5;
    $[28] = t8;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  return t15;
}
function PositionThumb(t0) {
  const $ = _c(21);
  const {
    row,
    hue,
    dim,
    showDot,
    sizeClass: t1,
    fallbackImageUrl,
    fallbackImageNsfw
  } = t0;
  const sizeClass = t1 === undefined ? "size-[46px]" : t1;
  const isWinner = row.positionType === "winner";
  const imageUrl = row.outcomeImageUrl ?? fallbackImageUrl ?? null;
  const imageNsfw = row.outcomeImageUrl !== null ? row.outcomeImageNsfw : fallbackImageNsfw ?? false;
  const t2 = dim && "opacity-55";
  let t3;
  if ($[0] !== sizeClass || $[1] !== t2) {
    t3 = cn("relative shrink-0", sizeClass, t2);
    $[0] = sizeClass;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== hue || $[4] !== imageNsfw || $[5] !== imageUrl || $[6] !== row.market || $[7] !== row.outcomeIndex) {
    t4 = {
      kind: "outcome",
      marketPubkey: row.market,
      outcomeIndex: row.outcomeIndex,
      hue,
      imageUrl,
      nsfw: imageNsfw
    };
    $[3] = hue;
    $[4] = imageNsfw;
    $[5] = imageUrl;
    $[6] = row.market;
    $[7] = row.outcomeIndex;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = row.outcomeLabel ?? "";
  let t6;
  if ($[9] !== sizeClass || $[10] !== t4 || $[11] !== t5) {
    t6 = <OptimizedImage variant="thumbnail" entity={t4} alt={t5} size={sizeClass} rounded="rounded-control" preview={false} />;
    $[9] = sizeClass;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== hue || $[14] !== isWinner || $[15] !== showDot) {
    t7 = showDot && <span aria-hidden={true} className={cn("absolute -right-1 -bottom-1 size-[13px] rounded-full border-2 border-background-primary", isWinner ? "bg-brand-400" : "bg-feedback-success-accent")} style={isWinner ? undefined : {
      backgroundColor: outcomeHueToStepVar(hue, 400)
    }} />;
    $[13] = hue;
    $[14] = isWinner;
    $[15] = showDot;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t3 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className={t3}>{t6}{t7}</div>;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}
function PnlValue(t0) {
  const $ = _c(16);
  const {
    d6,
    className
  } = t0;
  const up = d6 > BigInt(PNL_DUST_D6);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = BigInt(PNL_DUST_D6);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const down = d6 < -t1;
  const tone = up ? "text-data-profit" : down ? "text-data-loss" : "text-foreground-disabled";
  const magnitude = d6 < ZERO ? -d6 : d6;
  let t2;
  if ($[1] !== className || $[2] !== tone) {
    t2 = cn("mt-0.5 inline-flex items-center gap-1.5 text-heading-lg font-bold tabular-nums transition-colors", tone, className);
    $[1] = className;
    $[2] = tone;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== d6 || $[5] !== down || $[6] !== up) {
    t3 = (up || down) && <PnlDirectionArrow valueD6={d6} className="size-4" />;
    $[4] = d6;
    $[5] = down;
    $[6] = up;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== magnitude) {
    t4 = magnitude.toString();
    $[8] = magnitude;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <AnimatedD6 d6={t4} />;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t5) {
    t6 = <div className={t2}>{t3}{t5}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}

// Translucent stat tile — same card layout as the winner-page token stats
// (`winner-stat-tile.tsx`): label above value inside a `rounded-panel-row`
// `bg-background-tertiary/50` chip. `tone` colours the value for PnL cells.
function Stat(t0) {
  const $ = _c(11);
  const {
    testId,
    label,
    value,
    tone
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-caption text-foreground-tertiary">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = tone ?? "text-foreground-primary";
  let t3;
  if ($[2] !== t2) {
    t3 = cn("text-body-lg font-medium tabular-nums", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3 || $[5] !== value) {
    t4 = <span className={t3}>{value}</span>;
    $[4] = t3;
    $[5] = value;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1 || $[8] !== t4 || $[9] !== testId) {
    t5 = <div className="flex flex-col gap-1 rounded-panel-row bg-background-tertiary/50 px-3 py-3" data-testid={testId}>{t1}{t4}</div>;
    $[7] = t1;
    $[8] = t4;
    $[9] = testId;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function pnlTone(d6: bigint): string {
  if (d6 > BigInt(PNL_DUST_D6)) return 'text-data-profit';
  if (d6 < -BigInt(PNL_DUST_D6)) return 'text-data-loss';
  return 'text-foreground-disabled';
}
function sortDisplayPositionsActiveFirst(rows: readonly PositionCardRow[]): readonly PositionCardRow[] {
  return rows.toSorted((left, right) => {
    const activeDelta = Number(hasActiveSnapshotOutcomeBalance(right)) - Number(hasActiveSnapshotOutcomeBalance(left));
    if (activeDelta !== 0) return activeDelta;
    return left.outcomeIndex - right.outcomeIndex;
  });
}
function resolveDisplayPositionHeadState(rows: readonly PositionCardRow[], {
  winnerPage
}: {
  winnerPage: boolean;
}): 'held' | 'resolved' | 'closed' {
  if (rows.some(hasActiveSnapshotOutcomeBalance)) return 'held';
  if (winnerPage) return 'closed';
  if (rows.some(row => row.lifecycle === 'resolved' || row.lifecycle === 'graduated')) {
    return 'resolved';
  }
  return 'closed';
}
function rawBalanceD6(value: string | undefined): bigint | null {
  if (value === undefined || !/^\d+$/.test(value)) return null;
  return BigInt(value);
}